import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Socket } from 'socket.io-client';

// ============ EVENT EMITTER ============

class TerritoryOverviewEmitter {
  private listeners: Array<() => void> = [];

  on(callback: () => void): () => void {
    this.listeners.push(callback);
    return () => {
      this.listeners = this.listeners.filter(l => l !== callback);
    };
  }

  emit(): void {
    this.listeners.forEach(callback => callback());
  }
}

export const territoryOverviewEmitter = new TerritoryOverviewEmitter();

// ============ TYPES ============

interface TerritoryClaim {
  id: string;
  islandId: string;
  ownerId: string;
  ownerType: 'player' | 'crew';
  ownerName: string;
  customName: string | null;
  claimRadius: number;
  baseRadius: number;
  permissions: {
    allowAllies: boolean;
    allowTrading: boolean;
    allowDocking: boolean;
    pvpEnabled: boolean;
    taxRate: number;
  };
  upgrades: Array<{ type: string; level: number }>;
  upkeepPaidUntil: string | null;
  claimedAt: string;
  lastCollectedAt: string | null;
  accumulatedPieces: number;
  accumulatedMaterials: Record<string, number>;
}

interface Island {
  id: string;
  x: number;
  y: number;
  radius: number;
  type: string;
  biome: string;
  isPermanent: boolean;
  permanentName: string | null;
  customName: string | null;
}

interface Structure {
  id: string;
  type: string;
  level: number;
  isBuilding: boolean;
  health: number;
  maxHealth: number;
}

interface ResourceNode {
  id: string;
  type: string;
  currentAmount: number;
  maxAmount: number;
}

interface PassiveIncome {
  pieces: number;
  materials: Record<string, number>;
  hoursSinceCollection: number;
}

interface TerritoryData {
  claim: TerritoryClaim;
  island: Island;
  structures: Structure[];
  resourceNodes: ResourceNode[];
  passiveIncome: PassiveIncome;
}

// ============ COMPONENT ============

export const TerritoryOverviewPanel: React.FC = () => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [territories, setTerritories] = useState<TerritoryData[]>([]);
  const [selectedTerritoryId, setSelectedTerritoryId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);
  const [collectingFrom, setCollectingFrom] = useState<string | null>(null);
  const socketRef = useRef<Socket | null>(null);

  // Listen for emitter event to open panel
  useEffect(() => {
    const unsubscribe = territoryOverviewEmitter.on(() => {
      setIsOpen(true);
      setIsLoading(true);
    });
    return unsubscribe;
  }, []);

  // Get socket from GameCanvas and fetch territories when opening
  useEffect(() => {
    if (!isOpen) return;

    const getSocket = (): Socket | null => {
      // Try window.gameSocket first
      if ((window as any).gameSocket) {
        console.log('✅ Got socket from window.gameSocket');
        return (window as any).gameSocket;
      }
      
      // Try to get from Phaser game instance
      try {
        const game = (window as any).phaserGame;
        if (game && game.scene) {
          const scene = game.scene.getScene('OceanScene');
          if (scene && (scene as any).socket) {
            console.log('✅ Got socket from OceanScene');
            return (scene as any).socket;
          }
        }
      } catch (e) {
        // Silently fail, will retry
      }
      
      return null;
    };

    let socket = socketRef.current || getSocket();
    let retries = 0;
    const maxRetries = 20; // Increased from 5 to 20 (4 seconds total)

    const setupSocket = (): (() => void) | void => {
      socket = socketRef.current || getSocket();
      
      if (!socket) {
        if (retries < maxRetries) {
          retries++;
          setTimeout(setupSocket, 200);
        } else {
          console.error('Territory Overview: Socket not available after retries');
          setMessage({ text: 'Connection error - please try again', type: 'error' });
          setIsLoading(false);
        }
        return;
      }

      socketRef.current = socket;

      const handleOverview = (data: { territories: TerritoryData[] }): void => {
        setTerritories(data.territories);
        setIsLoading(false);
      };

      const handleError = (data: { message: string }): void => {
        setMessage({ text: data.message, type: 'error' });
        setIsLoading(false);
        setTimeout(() => setMessage(null), 3000);
      };

      const handleCollectSuccess = (data: { message: string; claimId: string }): void => {
        setMessage({ text: data.message, type: 'success' });
        setCollectingFrom(null);
        socket!.emit('territory:getOverview');
        setTimeout(() => setMessage(null), 3000);
      };

      const handleCollectError = (data: { message: string }): void => {
        setMessage({ text: data.message, type: 'error' });
        setCollectingFrom(null);
        setTimeout(() => setMessage(null), 3000);
      };

      socket.on('territory:overview', handleOverview);
      socket.on('territory:error', handleError);
      socket.on('territory:collectSuccess', handleCollectSuccess);
      socket.on('territory:collectError', handleCollectError);
      socket.emit('territory:getOverview');

      return () => {
        socket!.off('territory:overview', handleOverview);
        socket!.off('territory:error', handleError);
        socket!.off('territory:collectSuccess', handleCollectSuccess);
        socket!.off('territory:collectError', handleCollectError);
      };
    };

    return setupSocket();
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  const handleCollectIncome = useCallback((claimId: string): void => {
    const socket = socketRef.current;
    if (!socket || collectingFrom) return;
    setCollectingFrom(claimId);
    socket.emit('territory:collectPassiveIncome', { claimId });
  }, [collectingFrom]);

  const handleNavigateToIsland = useCallback((island: Island): void => {
    const socket = socketRef.current;
    if (!socket) return;
    socket.emit('worldmap:focus', { x: island.x, y: island.y });
    setIsOpen(false);
  }, []);

  const formatTime = (hours: number): string => {
    if (hours < 1) {
      return `${Math.floor(hours * 60)}m`;
    }
    if (hours < 24) {
      return `${Math.floor(hours)}h`;
    }
    const days = Math.floor(hours / 24);
    const remainingHours = Math.floor(hours % 24);
    return remainingHours > 0 ? `${days}d ${remainingHours}h` : `${days}d`;
  };

  const formatDate = (dateString: string | null): string => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString();
  };

  const getUpkeepStatus = (claim: TerritoryClaim): { text: string; color: string } => {
    if (!claim.upkeepPaidUntil) {
      return { text: 'Unpaid', color: '#f85149' };
    }
    const paidUntil = new Date(claim.upkeepPaidUntil);
    const now = new Date();
    const diffMs = paidUntil.getTime() - now.getTime();
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) {
      return { text: 'Overdue', color: '#f85149' };
    }
    if (diffDays < 3) {
      return { text: `${diffDays}d left`, color: '#f0883e' };
    }
    return { text: `${diffDays}d left`, color: '#3fb950' };
  };

  const getStructureIcon = (type: string): string => {
    const icons: Record<string, string> = {
      watchtower: '👁️',
      dock: '⚓',
      warehouse: '📦',
      cannon_battery: '🔫',
      refinery: '⚙️',
      shipyard: '🚢',
    };
    return icons[type] || '🏗️';
  };

  const getResourceIcon = (type: string): string => {
    const icons: Record<string, string> = {
      timber: '🪵',
      ore: '⛏️',
      sulfur: '💨',
      whale_oil: '🐋',
      herbs: '🌿',
      gems: '💎',
    };
    return icons[type] || '📦';
  };

  const getBiomeColor = (biome: string): string => {
    const colors: Record<string, string> = {
      tropical: '#2ecc71',
      temperate: '#3498db',
      arctic: '#ecf0f1',
      volcanic: '#e74c3c',
      desert: '#f39c12',
      jungle: '#27ae60',
    };
    return colors[biome] || '#95a5a6';
  };

  if (!isOpen) return null;

  const selectedTerritory = territories.find(t => t.claim.id === selectedTerritoryId);
  const totalPieces = territories.reduce((sum, t) => sum + t.passiveIncome.pieces, 0);
  const totalStructures = territories.reduce((sum, t) => sum + t.structures.length, 0);
  const totalResources = territories.reduce((sum, t) => sum + t.resourceNodes.length, 0);

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.85)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
      }}
      onClick={() => setIsOpen(false)}
    >
      <div
        style={{
          backgroundColor: '#0d1117',
          border: '2px solid #30363d',
          width: '900px',
          maxHeight: '85vh',
          overflow: 'hidden',
          fontFamily: '"Sometype Mono", monospace',
          display: 'flex',
          flexDirection: 'column',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '12px 16px',
            borderBottom: '1px solid #30363d',
            backgroundColor: '#161b22',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ color: '#58a6ff', fontSize: '16px', fontWeight: 'bold' }}>
              TERRITORY OVERVIEW
            </span>
            <span style={{ color: '#8b949e', fontSize: '11px' }}>
              {territories.length} {territories.length === 1 ? 'Island' : 'Islands'}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setIsOpen(false);
              }}
              style={{
                backgroundColor: '#dc2626',
                border: '1px solid #ef4444',
                color: '#fff',
                cursor: 'pointer',
                padding: '6px 12px',
                fontSize: '12px',
                fontWeight: 'bold',
                fontFamily: '"Sometype Mono", monospace',
              }}
            >
              ×
            </button>
          </div>
        </div>

        {/* Summary Stats */}
        <div
          style={{
            display: 'flex',
            gap: '12px',
            padding: '12px 16px',
            backgroundColor: '#161b22',
            borderBottom: '1px solid #30363d',
          }}
        >
          <div style={{ flex: 1, textAlign: 'center' }}>
            <div style={{ color: '#8b949e', fontSize: '10px', marginBottom: '4px' }}>
              TERRITORIES
            </div>
            <div style={{ color: '#58a6ff', fontSize: '18px', fontWeight: 'bold' }}>
              {territories.length}
            </div>
          </div>
          <div style={{ flex: 1, textAlign: 'center' }}>
            <div style={{ color: '#8b949e', fontSize: '10px', marginBottom: '4px' }}>
              STRUCTURES
            </div>
            <div style={{ color: '#3fb950', fontSize: '18px', fontWeight: 'bold' }}>
              {totalStructures}
            </div>
          </div>
          <div style={{ flex: 1, textAlign: 'center' }}>
            <div style={{ color: '#8b949e', fontSize: '10px', marginBottom: '4px' }}>
              RESOURCES
            </div>
            <div style={{ color: '#f0883e', fontSize: '18px', fontWeight: 'bold' }}>
              {totalResources}
            </div>
          </div>
          <div style={{ flex: 1, textAlign: 'center' }}>
            <div style={{ color: '#8b949e', fontSize: '10px', marginBottom: '4px' }}>
              READY TO COLLECT
            </div>
            <div style={{ color: '#ffd700', fontSize: '18px', fontWeight: 'bold' }}>
              {totalPieces.toLocaleString()}₱
            </div>
          </div>
        </div>

        {/* Message */}
        {message && (
          <div
            style={{
              padding: '10px 16px',
              backgroundColor: message.type === 'success' ? '#1c3d2e' : '#3d1c1c',
              borderBottom: `1px solid ${message.type === 'success' ? '#3fb950' : '#f85149'}`,
              color: message.type === 'success' ? '#3fb950' : '#f85149',
              fontSize: '12px',
              fontWeight: 'bold',
            }}
          >
            {message.text}
          </div>
        )}

        {/* Content */}
        <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          {/* Territory List */}
          <div
            style={{
              width: '350px',
              borderRight: '1px solid #30363d',
              overflow: 'auto',
              backgroundColor: '#0d1117',
            }}
          >
            {isLoading ? (
              <div style={{ padding: '20px', textAlign: 'center', color: '#8b949e', fontSize: '12px' }}>
                Loading territories...
              </div>
            ) : territories.length === 0 ? (
              <div style={{ padding: '20px', textAlign: 'center', color: '#8b949e', fontSize: '12px' }}>
                You don't own any territories yet.
                <br />
                <br />
                Claim an island to start building your empire!
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', padding: '12px' }}>
                {territories.map((territory) => {
                  const isSelected = selectedTerritoryId === territory.claim.id;
                  const upkeepStatus = getUpkeepStatus(territory.claim);
                  const hasIncome = territory.passiveIncome.pieces > 0 || Object.keys(territory.passiveIncome.materials).length > 0;
                  const islandName = territory.claim.customName || territory.island.permanentName || `Island ${territory.island.id.slice(0, 8)}`;

                  return (
                    <div
                      key={territory.claim.id}
                      onClick={() => setSelectedTerritoryId(isSelected ? null : territory.claim.id)}
                      style={{
                        backgroundColor: isSelected ? '#1c2128' : '#161b22',
                        border: `1px solid ${isSelected ? '#58a6ff' : '#30363d'}`,
                        padding: '12px',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ color: '#c9d1d9', fontSize: '13px', fontWeight: 'bold', marginBottom: '4px' }}>
                            {islandName}
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                            <span
                              style={{
                                color: getBiomeColor(territory.island.biome),
                                fontSize: '10px',
                                padding: '2px 6px',
                                backgroundColor: '#21262d',
                                borderRadius: '2px',
                              }}
                            >
                              {territory.island.biome.toUpperCase()}
                            </span>
                            <span style={{ color: '#8b949e', fontSize: '10px' }}>
                              {territory.island.type}
                            </span>
                          </div>
                        </div>
                        {hasIncome && (
                          <div
                            style={{
                              backgroundColor: '#1c3d2e',
                              border: '1px solid #3fb950',
                              padding: '4px 8px',
                              borderRadius: '2px',
                            }}
                          >
                            <div style={{ color: '#ffd700', fontSize: '11px', fontWeight: 'bold' }}>
                              {territory.passiveIncome.pieces.toLocaleString()}₱
                            </div>
                          </div>
                        )}
                      </div>

                      <div style={{ display: 'flex', gap: '8px', fontSize: '10px', color: '#8b949e', marginBottom: '6px' }}>
                        <span>🏗️ {territory.structures.length}</span>
                        <span>📦 {territory.resourceNodes.length}</span>
                        <span style={{ color: upkeepStatus.color }}>
                          ⏰ {upkeepStatus.text}
                        </span>
                      </div>

                      {isSelected && (
                        <div style={{ display: 'flex', gap: '6px', marginTop: '8px' }}>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleNavigateToIsland(territory.island);
                            }}
                            style={{
                              flex: 1,
                              padding: '6px',
                              backgroundColor: '#238636',
                              color: '#ffffff',
                              border: 'none',
                              cursor: 'pointer',
                              fontSize: '10px',
                              fontWeight: 'bold',
                              fontFamily: '"Sometype Mono", monospace',
                            }}
                          >
                            VIEW ON MAP
                          </button>
                          {hasIncome && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCollectIncome(territory.claim.id);
                              }}
                              disabled={collectingFrom === territory.claim.id}
                              style={{
                                flex: 1,
                                padding: '6px',
                                backgroundColor: collectingFrom === territory.claim.id ? '#21262d' : '#1f6feb',
                                color: collectingFrom === territory.claim.id ? '#8b949e' : '#ffffff',
                                border: 'none',
                                cursor: collectingFrom === territory.claim.id ? 'not-allowed' : 'pointer',
                                fontSize: '10px',
                                fontWeight: 'bold',
                                fontFamily: '"Sometype Mono", monospace',
                              }}
                            >
                              {collectingFrom === territory.claim.id ? 'COLLECTING...' : 'COLLECT'}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Details Panel */}
          <div style={{ flex: 1, overflow: 'auto', padding: '16px', backgroundColor: '#0d1117' }}>
            {!selectedTerritory ? (
              <div style={{ textAlign: 'center', color: '#8b949e', fontSize: '12px', marginTop: '40px' }}>
                Select a territory to view details
              </div>
            ) : (
              <div>
                {/* Territory Info */}
                <div style={{ marginBottom: '20px' }}>
                  <div style={{ color: '#8b949e', fontSize: '10px', marginBottom: '8px' }}>
                    TERRITORY INFORMATION
                  </div>
                  <div
                    style={{
                      backgroundColor: '#161b22',
                      border: '1px solid #30363d',
                      padding: '12px',
                    }}
                  >
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '11px' }}>
                      <div>
                        <span style={{ color: '#8b949e' }}>Claimed:</span>{' '}
                        <span style={{ color: '#c9d1d9' }}>{formatDate(selectedTerritory.claim.claimedAt)}</span>
                      </div>
                      <div>
                        <span style={{ color: '#8b949e' }}>Claim Radius:</span>{' '}
                        <span style={{ color: '#c9d1d9' }}>{Math.floor(selectedTerritory.claim.claimRadius)}px</span>
                      </div>
                      <div>
                        <span style={{ color: '#8b949e' }}>PvP:</span>{' '}
                        <span style={{ color: selectedTerritory.claim.permissions.pvpEnabled ? '#f85149' : '#3fb950' }}>
                          {selectedTerritory.claim.permissions.pvpEnabled ? 'Enabled' : 'Disabled'}
                        </span>
                      </div>
                      <div>
                        <span style={{ color: '#8b949e' }}>Tax Rate:</span>{' '}
                        <span style={{ color: '#c9d1d9' }}>{selectedTerritory.claim.permissions.taxRate}%</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Passive Income */}
                {(selectedTerritory.passiveIncome.pieces > 0 || Object.keys(selectedTerritory.passiveIncome.materials).length > 0) && (
                  <div style={{ marginBottom: '20px' }}>
                    <div style={{ color: '#8b949e', fontSize: '10px', marginBottom: '8px' }}>
                      READY TO COLLECT
                    </div>
                    <div
                      style={{
                        backgroundColor: '#1c3d2e',
                        border: '1px solid #3fb950',
                        padding: '12px',
                      }}
                    >
                      <div style={{ color: '#ffd700', fontSize: '14px', fontWeight: 'bold', marginBottom: '8px' }}>
                        {selectedTerritory.passiveIncome.pieces.toLocaleString()}₱
                      </div>
                      {Object.entries(selectedTerritory.passiveIncome.materials).length > 0 && (
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                          {Object.entries(selectedTerritory.passiveIncome.materials).map(([type, amount]) => (
                            <span
                              key={type}
                              style={{
                                color: '#3fb950',
                                fontSize: '11px',
                                padding: '4px 8px',
                                backgroundColor: '#21262d',
                                borderRadius: '2px',
                              }}
                            >
                              {getResourceIcon(type)} {amount} {type}
                            </span>
                          ))}
                        </div>
                      )}
                      <div style={{ color: '#8b949e', fontSize: '10px', marginTop: '8px' }}>
                        Accumulated over {formatTime(selectedTerritory.passiveIncome.hoursSinceCollection)}
                      </div>
                    </div>
                  </div>
                )}

                {/* Upgrades */}
                {selectedTerritory.claim.upgrades.length > 0 && (
                  <div style={{ marginBottom: '20px' }}>
                    <div style={{ color: '#8b949e', fontSize: '10px', marginBottom: '8px' }}>
                      TERRITORY UPGRADES
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      {selectedTerritory.claim.upgrades.map((upgrade) => (
                        <div
                          key={upgrade.type}
                          style={{
                            backgroundColor: '#161b22',
                            border: '1px solid #30363d',
                            padding: '8px 12px',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                          }}
                        >
                          <span style={{ color: '#c9d1d9', fontSize: '11px', textTransform: 'capitalize' }}>
                            {upgrade.type.replace(/_/g, ' ')}
                          </span>
                          <span style={{ color: '#58a6ff', fontSize: '11px', fontWeight: 'bold' }}>
                            Level {upgrade.level}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Structures */}
                <div style={{ marginBottom: '20px' }}>
                  <div style={{ color: '#8b949e', fontSize: '10px', marginBottom: '8px' }}>
                    STRUCTURES ({selectedTerritory.structures.length})
                  </div>
                  {selectedTerritory.structures.length === 0 ? (
                    <div
                      style={{
                        backgroundColor: '#161b22',
                        border: '1px solid #30363d',
                        padding: '12px',
                        textAlign: 'center',
                        color: '#8b949e',
                        fontSize: '11px',
                      }}
                    >
                      No structures built yet
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      {selectedTerritory.structures.map((structure) => (
                        <div
                          key={structure.id}
                          style={{
                            backgroundColor: '#161b22',
                            border: '1px solid #30363d',
                            padding: '10px 12px',
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span style={{ fontSize: '14px' }}>{getStructureIcon(structure.type)}</span>
                              <span style={{ color: '#c9d1d9', fontSize: '12px', textTransform: 'capitalize' }}>
                                {structure.type.replace(/_/g, ' ')}
                              </span>
                              <span style={{ color: '#8b949e', fontSize: '10px' }}>
                                Lv.{structure.level}
                              </span>
                            </div>
                            {structure.isBuilding ? (
                              <span style={{ color: '#f0883e', fontSize: '10px' }}>BUILDING...</span>
                            ) : (
                              <span style={{ color: '#8b949e', fontSize: '10px' }}>
                                HP: {structure.health}/{structure.maxHealth}
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Resource Nodes */}
                <div>
                  <div style={{ color: '#8b949e', fontSize: '10px', marginBottom: '8px' }}>
                    RESOURCE NODES ({selectedTerritory.resourceNodes.length})
                  </div>
                  {selectedTerritory.resourceNodes.length === 0 ? (
                    <div
                      style={{
                        backgroundColor: '#161b22',
                        border: '1px solid #30363d',
                        padding: '12px',
                        textAlign: 'center',
                        color: '#8b949e',
                        fontSize: '11px',
                      }}
                    >
                      No resource nodes on this island
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      {selectedTerritory.resourceNodes.map((node) => {
                        const percentage = Math.floor((node.currentAmount / node.maxAmount) * 100);
                        const isReady = percentage > 75;

                        return (
                          <div
                            key={node.id}
                            style={{
                              backgroundColor: '#161b22',
                              border: '1px solid #30363d',
                              padding: '10px 12px',
                            }}
                          >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span style={{ fontSize: '14px' }}>{getResourceIcon(node.type)}</span>
                                <span style={{ color: '#c9d1d9', fontSize: '12px', textTransform: 'capitalize' }}>
                                  {node.type}
                                </span>
                              </div>
                              <span style={{ color: isReady ? '#3fb950' : '#8b949e', fontSize: '11px' }}>
                                {node.currentAmount}/{node.maxAmount}
                              </span>
                            </div>
                            <div
                              style={{
                                width: '100%',
                                height: '4px',
                                backgroundColor: '#21262d',
                                borderRadius: '2px',
                                overflow: 'hidden',
                              }}
                            >
                              <div
                                style={{
                                  width: `${percentage}%`,
                                  height: '100%',
                                  backgroundColor: isReady ? '#3fb950' : '#f0883e',
                                  transition: 'width 0.3s',
                                }}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
